import { DateTime } from "luxon";
import { z } from "zod";

// Types correspond to vpoc/apps/calendar_integration data models

// Standardized calendar event attendee model for both Google and Outlook calendars
export type CalendarEventAttendee = {
    name: string;
    email: string;
    status: string;
    optional: boolean;
};

export type CalendarIntegrationEvent = {
    provider: "google" | "outlook";
    event_id: string;
    subject: string | null;
    description: string | null;
    start_time: string | null; // ISO datetime string
    end_time: string | null; // ISO datetime string
    attendees: CalendarEventAttendee[];
    recurrence: boolean;
    response_status: string;
    is_organizer: boolean;
    all_day: boolean;
    is_private: boolean;
};

// Calendar event with view-specific fields (meeting tips, coaching session info)
export type CalendarIntegrationViewEvent = CalendarIntegrationEvent & {
    meeting_tip: string | null;
    has_coaching_session: boolean;
};

// Input type for API requests - fields with defaults in Pydantic model can be undefined
export type CalendarIntegrationEventInput = Pick<CalendarIntegrationEvent, "provider" | "event_id"> & Partial<Omit<CalendarIntegrationEvent, "provider" | "event_id">>;

// Types correspond to data that is handled by frontend components
export type ProcessedCalendarEvent = Pick<CalendarIntegrationViewEvent, "provider" | "event_id" | "attendees" | "meeting_tip" | "has_coaching_session" | "description"> & {
    title: string;
    startTime: DateTime;
    endTime: DateTime;
};

export type CalendarInsightsPromptCallCode = "calendar_insights_engine";

export type CalendarInsightsChatPromptCallCode = "calendar_insights_dive_deeper";

export const CalendarInsightSchema = z.object({
    insight_id: z.string(),
    experiment_id: z.string(),
    title: z.string(),
    summary: z.string(),
    content: z.string(),
    confidence_level: z.number(),
});

export type CalendarInsightData = z.infer<typeof CalendarInsightSchema>;

// Response type for get_calendar_insights API
// Corresponds to CalendarInsightsResponse in calendar_insights.py
export type CalendarInsightsResponse = {
    insight: CalendarInsightData | null;
};

// Request type for create_calendar_insights_conversation API
// Corresponds to CreateCalendarInsightChatApi in calendar_insights.py
export type CreateCalendarInsightsConversationRequest = {
    insight_id: string;
    prompt_call_code?: CalendarInsightsChatPromptCallCode;
};

// Response type for create_calendar_insights_conversation API
// Corresponds to CreateCalendarInsightsConversationResponse in calendar_insights.py
export type CreateCalendarInsightsConversationResponse = {
    prompt_call_code: CalendarInsightsChatPromptCallCode;
    coaching_session_id: number;
    coaching_session_url: string;
};
